import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { MapPin, Star, Clock, Users, Camera, Phone, Car, Heart } from "lucide-react";

const KhajranaGaneshTemple = () => {
  const navigate = useNavigate();
  const templeImage = "/Indore/temples/khajrana0.jpg";
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Khajrana Ganesh Temple",
    "description": "Famous Ganesh temple in Indore known for fulfilling wishes and attracting millions of devotees",
    "image": templeImage,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Khajrana Road",
      "addressLocality": "Indore",
      "addressRegion": "Madhya Pradesh",
      "addressCountry": "India"
    },
    "openingHours": "Mo-Su 05:00-22:00",
    "priceRange": "Free",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "15000"
    }
  };

  const highlights = [
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Wish Fulfilling Temple",
      description: "One of the most revered Ganesh temples in India, famous for fulfilling devotees' wishes"
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: "Sacred Gold Plated Idol",
      description: "Beautiful gold-plated Ganesh idol that attracts millions of devotees annually"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Spiritual Atmosphere",
      description: "Peaceful environment with continuous prayers, bhajans, and spiritual activities"
    },
    {
      icon: <Camera className="w-6 h-6" />,
      title: "Religious Architecture",
      description: "Beautiful temple architecture with intricate designs and traditional decorations"
    }
  ];

  const visitInfo = [
    { label: "Entry Fee", value: "Free for all visitors" },
    { label: "Timings", value: "5:00 AM - 10:00 PM (All days)" },
    { label: "Aarti Times", value: "Morning 6 AM, Evening 7 PM" },
    { label: "Best Time", value: "Early morning or evening" },
    { label: "Duration", value: "30 minutes - 1 hour" },
    { label: "Facilities", value: "Parking, Prasad shop, Shoe keeping" }
  ];

  const festivals = [
    { name: "Ganesh Chaturthi", time: "August/September", special: "Grand celebrations with elaborate decorations" },
    { name: "Maghi Ganesh Jayanti", time: "January/February", special: "Special prayers and offerings" },
    { name: "Sankashti Chaturthi", time: "Monthly", special: "Special evening prayers and fasting" },
    { name: "Vinayaka Chaturthi", time: "Monthly", special: "Monthly celebrations with devotional activities" }
  ];

  const nearbyAttractions = [
    { name: "Rajwada Palace", distance: "3 km", time: "10 mins" },
    { name: "Lal Bagh Palace", distance: "4 km", time: "12 mins" },
    { name: "Sarafa Bazaar", distance: "3.5 km", time: "12 mins" },
    { name: "Central Museum", distance: "2.5 km", time: "8 mins" }
  ];

  return (
    <>
      <Helmet>
        <title>Khajrana Ganesh Temple Indore - Wish Fulfilling Ganesha | Aaoghumen</title>
        <meta name="description" content="Visit Khajrana Ganesh Temple in Indore with Aaoghumen - famous wish-fulfilling Ganesha temple with gold-plated idol. Complete guide with timings, aarti schedule, and spiritual significance." />
        <meta name="keywords" content="khajrana ganesh temple indore, khajrana temple timings, indore ganesh temple, wish fulfilling ganpati, aaoghumen khajrana, indore temples, khajrana aarti timings, ganesh chaturthi indore" />
        
        <meta property="og:title" content="Khajrana Ganesh Temple Indore - Divine Blessings | Aaoghumen" />
        <meta property="og:description" content="Experience divine blessings at Khajrana Ganesh Temple, one of India's most revered Ganesha temples in Indore known for fulfilling devotees' wishes." />
        <meta property="og:image" content={templeImage} />
        <meta name="twitter:image" content={templeImage} />
        <meta property="og:url" content="https://aaoghumen.com/destinations/indore/khajrana-ganesh-temple" />
        
        <link rel="canonical" href="https://aaoghumen.com/destinations/indore/khajrana-ganesh-temple" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-yellow-50">
        {/* Hero Section */}
        <div className="relative h-96 bg-gradient-to-r from-orange-600 via-yellow-500 to-red-600 overflow-hidden">
          <img
            src={templeImage}
            alt="Khajrana Ganesh Temple Indore"
            className="absolute inset-0 w-full h-full object-cover object-center opacity-70"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-5xl font-bold mb-4">
                <span className="text-yellow-300">Khajrana Ganesh Temple</span> - Indore
              </h1>
              <p className="text-xl mb-6">
                Experience divine blessings at one of India's most revered Ganesha temples, famous for fulfilling devotees' wishes and spreading spiritual joy throughout Indore.
              </p>
              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  <span>4.8 Rating</span>
                </div>
                <div className="flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>5 AM - 10 PM</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  <span>Free Entry</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          {/* About Section */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About Khajrana Ganesh Temple</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Khajrana Ganesh Temple stands as one of the most revered and spiritually significant temples in Indore, attracting millions of devotees from across India and beyond. This sacred temple is renowned for its wish-fulfilling powers, with countless devotees sharing stories of their prayers being answered by Lord Ganesha. The temple houses a magnificent gold-plated idol of Lord Ganesha that radiates divine energy and creates an atmosphere of profound spirituality.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                The temple's history dates back several decades, and it has grown from a small shrine to one of the most visited religious destinations in Madhya Pradesh. The temple complex features beautiful traditional architecture with intricate carvings and decorations that enhance the spiritual ambiance. The continuous chanting of prayers, devotional music, and the fragrance of incense create an atmosphere that transports visitors into a state of divine connection.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                With Aaoghumen, discover the spiritual significance of this sacred temple, participate in traditional rituals, and experience the peaceful energy that has made Khajrana Ganesh Temple a beacon of faith for devotees worldwide. The temple's reputation for fulfilling wishes has made it an essential pilgrimage destination for anyone seeking Lord Ganesha's blessings.
              </p>
            </div>
          </section>

          {/* Highlights */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Temple Highlights</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {highlights.map((highlight, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="bg-orange-100 p-3 rounded-lg mr-4">
                      {highlight.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{highlight.title}</h3>
                  </div>
                  <p className="text-gray-600">{highlight.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Visit Information */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Visitor Information</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visitInfo.map((info, index) => (
                  <div key={index} className="border-l-4 border-orange-500 pl-4">
                    <h3 className="font-bold text-gray-800 mb-2">{info.label}</h3>
                    <p className="text-gray-600">{info.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Festivals and Celebrations */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Festivals & Special Occasions</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {festivals.map((festival, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-xl font-bold text-orange-600 mb-2">{festival.name}</h3>
                  <p className="text-gray-500 mb-3">{festival.time}</p>
                  <p className="text-gray-600">{festival.special}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Spiritual Experiences */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Spiritual Experiences</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-orange-600 mb-4">Morning Aarti</h3>
                <p className="text-gray-600 mb-4">Begin your day with the divine morning aarti at 6 AM, experiencing the temple's peaceful morning atmosphere.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• 6:00 AM daily</li>
                  <li>• Peaceful atmosphere</li>
                  <li>• Divine blessings</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-orange-600 mb-4">Evening Prayers</h3>
                <p className="text-gray-600 mb-4">Join the evening aarti at 7 PM with devotional songs and prayers that create a magical spiritual ambiance.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• 7:00 PM daily</li>
                  <li>• Group prayers</li>
                  <li>• Devotional music</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-xl font-bold text-orange-600 mb-4">Wish Rituals</h3>
                <p className="text-gray-600 mb-4">Participate in special wish-fulfilling rituals and offering ceremonies guided by temple priests.</p>
                <ul className="text-sm text-gray-500 space-y-1">
                  <li>• Special prayers available</li>
                  <li>• Guided by priests</li>
                  <li>• Traditional offerings</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Nearby Attractions */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">Nearby Attractions</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {nearbyAttractions.map((attraction, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 cursor-pointer">
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{attraction.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-orange-500" />
                      <span>{attraction.distance}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Car className="w-4 h-4 mr-2 text-blue-500" />
                      <span>{attraction.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* How to Reach */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-gray-800 mb-8">How to Reach</h2>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Public Transport</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Bus:</strong> City buses to Khajrana, well-connected route</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Auto:</strong> Available from all parts of city, ₹40-100</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Taxi:</strong> Uber/Ola readily available</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">By Private Vehicle</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Parking:</strong> Large parking area available</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Location:</strong> Khajrana Road, easily accessible</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></span>
                      <span><strong>Navigation:</strong> Search "Khajrana Ganesh Temple" on maps</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Contact and Plan Visit */}
          <section className="bg-gradient-to-r from-orange-600 to-yellow-600 rounded-2xl shadow-lg p-8 text-white">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Experience Divine Blessings</h2>
              <p className="text-xl mb-6">Visit Khajrana Ganesh Temple with spiritual guidance and local insights</p>
              <div className="flex justify-center items-center space-x-8 mb-6">
                <div className="flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  <span>+91 97139 08893</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  <span>Khajrana Road, Indore</span>
                </div>
              </div>
              <div className="flex justify-center space-x-4">
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="bg-white text-orange-600 font-bold py-3 px-6 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Plan Temple Visit
                </button>
                <button 
                  onClick={() => navigate('/onboarding')}
                  className="border-2 border-white text-white font-bold py-3 px-6 rounded-full hover:bg-white hover:text-orange-600 transition-colors"
                >
                  Get Directions
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default KhajranaGaneshTemple;